import store from "@/store";
// Store
import { ConversationAction, ConversationMutation } from "@/framework/store/conversation/types";
import { AuthAction } from "@/framework/store/auth/types";
import { Action as HotelAction } from "@/framework/store/hotel/types";
// Types
import { Message } from "@/modules/staff/objects/conversation/Message";
import {
  ChangeUser,
  ConversationState,
  CreateQcUnreadConversation,
  CreateQcUnrepliedConversation,
  EventType,
  NotifyMyEvent,
  NotifyMyself,
  OnRespondVoiceChatInRoom,
  OnStartVoiceChatInRoom,
  ReadConversation,
  SandboxResult,
} from "@/framework/services/events/subscription";
// Services
import { IProjectService, KtznProject, ProjectService } from "@/framework/services/general/projectService";
// Helpers
import { checkExistInquiry } from "@/modules/staff/services/inquiry";
import { isEmpty } from "@/framework/utilities/isEmpty";
import { ErrorService } from "@/framework/errors/ErrorService";
import { ConversationService } from "@/framework/api/Staff/ChatService/ConversationService";
import { QcUnreadMessage } from "@/modules/staff/objects/conversation/QcUnreadMessage";
import { EarliestInquiryInfo } from "@/modules/staff/objects/conversation/InquiryObjects";

export interface INotifyMyEventService {
  /**
   * Parses the general `NotifyMyEvent` and calls an event handler depending on the event type.
   * @param event NotifyMyEvent
   */
  parse(event: NotifyMyEvent): Promise<void>;

  onCreateMessageHandler(
    onCreateMessage: Message,
    currentConversationId: string,
    currentStaffConversationId: string
  ): void;

  onUpdateMessageHandler(
    onUpdateMessage: Message,
    currentConversationId: string,
    currentStaffConversationId: string
  ): void;

  onCreateQcUnreadMessageHandler(qcUnreadMessage: QcUnreadMessage): void;

  onReadConversationHandler(readConversation: ReadConversation): void;

  onUpdateConversationStateHandler(conversationState: ConversationState): Promise<void>;

  onNotifyMyselfHandler(event: NotifyMyself): void;

  onChangeUserHandler(changeUser: ChangeUser): void;

  onNotifyInquiryHandler(onNotifyInquiry: EarliestInquiryInfo): Promise<void>;

  testHandler(event: string): void;

  onCreateSandboxHandler(event: SandboxResult): void;

  onApplySandboxHandler(event: SandboxResult): void;

  onCreateQcUnreadConversation(event: CreateQcUnreadConversation): Promise<void>;

  onCreateQcUnrepliedConversationHandler(createUnrepliedConv: CreateQcUnrepliedConversation): Promise<void>;

  onStartVoiceChatInRoomHandler(data: OnStartVoiceChatInRoom): void;

  onRespondVoiceChatInRoomHandler(data: OnRespondVoiceChatInRoom): void;

  onDisplayNewIcon(data: any, conversationId: string): Promise<void>;
}

/**
 * Parses the general Kotoznza `NotifyMyEvent` that is received from the Kotozna backend.
 */
export class NotifyMyEventService implements INotifyMyEventService {
  private readonly project: KtznProject;

  private _projectService: IProjectService;

  public constructor(projectService: IProjectService = new ProjectService()) {
    this._projectService = projectService;

    // Get the current project
    const project = this._projectService.getCurrent();
    if (project) {
      this.project = project;
    } else {
      throw new Error("Error in NotifyMyEventService constructor: KtznProject not found.");
    }
  }

  /**
   * Parses the general `NotifyMyEvent` and calls an event handler depending on the event type.
   * @param event NotifyMyEvent
   */
  parse = async (event: NotifyMyEvent): Promise<void> => {
    const eventType = event.event_type as EventType;
    // decode base64
    const _event_value = event.event_value;
    const currentConversationId = store.getters.currentConversationId;
    const currentStaffConversationId = store.getters.currentConversationIdWithStaff;

    switch (eventType) {
      case EventType.OnCreateMessage: {
        this.onCreateMessageHandler(
          _event_value as unknown as Message,
          currentConversationId,
          currentStaffConversationId
        );
        return;
      }
      case EventType.OnUpdateMessage: {
        this.onUpdateMessageHandler(
          _event_value as unknown as Message,
          currentConversationId,
          currentStaffConversationId
        );
        return;
      }
      case EventType.OnCreateQcUnreadMessage: {
        this.onCreateQcUnreadMessageHandler(_event_value as unknown as QcUnreadMessage);
        return;
      }
      case EventType.OnDeleteQcUnreadMessage: {
        this.onDeleteQcUnreadMessageHandler(_event_value as unknown as QcUnreadMessage);
        return;
      }
      case EventType.OnReadConversation: {
        this.onReadConversationHandler(_event_value as unknown as ReadConversation);
        return;
      }
      case EventType.OnUpdateConversationState: {
        await this.onUpdateConversationStateHandler(_event_value as unknown as ConversationState);
        return;
      }
      case EventType.OnNotifyMyself: {
        this.onNotifyMyselfHandler(_event_value as unknown as NotifyMyself);
        return;
      }
      case EventType.OnChangeUser: {
        this.onChangeUserHandler(_event_value as unknown as ChangeUser);
        return;
      }
      case EventType.OnNotifyInquiry: {
        await this.onNotifyInquiryHandler(_event_value as unknown as EarliestInquiryInfo);
        return;
      }
      case EventType.Test: {
        this.testHandler(_event_value);
        return;
      }
      case EventType.OnCreateSandbox: {
        await this.onCreateSandboxHandler(_event_value as unknown as SandboxResult);
        return;
      }
      case EventType.OnApplySandbox: {
        await this.onApplySandboxHandler(_event_value as unknown as SandboxResult);
        return;
      }
      case EventType.OnCreateQcUnreadConversation: {
        await this.onCreateQcUnreadConversation(_event_value as unknown as CreateQcUnreadConversation);
        return;
      }
      case EventType.OnCreateQcUnrepliedConversation: {
        await this.onCreateQcUnrepliedConversationHandler(_event_value as unknown as CreateQcUnrepliedConversation);
        return;
      }
      case EventType.OnStartVoiceChatInRoom: {
        this.onStartVoiceChatInRoomHandler(_event_value as unknown as OnStartVoiceChatInRoom);
        return;
      }
      case EventType.OnRespondVoiceChatInRoom: {
        this.onRespondVoiceChatInRoomHandler(_event_value as unknown as OnRespondVoiceChatInRoom);
        return;
      }
      case EventType.OnChatTyping: {
        const data = _event_value as unknown;
        await this.onChatTyping(data as unknown as any);
        return;
      }
      case EventType.OnDisplayNewIcon: {
        await this.onDisplayNewIcon(_event_value as unknown as any);
        return;
      }
      default:
        throw new TypeError("Unsupported eventType: " + JSON.stringify(eventType));
    }
  };

  /**
   * Handles event for the incoming newly created chat messages
   * @param onCreateMessage
   * @param currentConversationId
   * @param currentStaffConversationId
   */
  onCreateMessageHandler = (
    onCreateMessage: Message,
    currentConversationId: string,
    currentStaffConversationId: string
  ): void => {
    console.log("Subscribe onCreateMessage: ", onCreateMessage);
    // due to storing the date as a string on creation, and the display of the string being in local time
    // this displays new message createdAt dates as the current date, but with UTC offset, creating bad date information
    // fix this by setting new store instances with the proper ISO string on creation
    onCreateMessage.createdAt = new Date(onCreateMessage.createdAt).toISOString();
    if (
      onCreateMessage.messageConversationId !== store.getters.currentConversationId &&
      this.project === KtznProject.Staff
    ) {
      return;
    }

    const conversationId = onCreateMessage.messageConversationId;
    if (conversationId === currentConversationId) {
      if (this.project === KtznProject.Staff) {
        // Hack to display system messages translations in staff chat
        onCreateMessage.lamondoSystemMessage = onCreateMessage.content;
      }
      store.dispatch(ConversationAction.addMessageToCurrentMessages, onCreateMessage);
    } else if (conversationId === currentStaffConversationId) {
      store.dispatch(ConversationAction.addMessageToCurrentMessagesWithStaff, onCreateMessage);
    } else {
      console.log("Error in onCreateMessage. Conversation is not current or staff.");
    }
  };

  /**
   * Handles event to update a message
   * @param onUpdateMessage Message event
   * @param currentConversationId Current conversation id
   * @param currentStaffConversationId Current staff conversation id
   */
  onUpdateMessageHandler = (
    onUpdateMessage: Message,
    currentConversationId: string,
    currentStaffConversationId: string
  ): void => {
    console.log("Subscribe onUpdateMessage: ", onUpdateMessage);
    //updateMessage is also immediately run after createMessage,
    //as well as on receiving messages from the other chat,
    //requiring the date correction in this event, as well
    if (onUpdateMessage.llmVersion && store.getters.currentMessages) {
      // in case of message from ChatGPT: (means: the llmVersion property has value and is not null!
      // We need to check the length of current message which we already have(received and proceed with it)
      // with the other one that we just received from BE, just now!
      // Reason: we might receive events in wrong sequences!
      // console.log('HERE', onUpdateMessage.id, onUpdateMessage.content)
      const msg: Array<Message> = store.getters.currentMessages.filter((x: Message) => {
        return x.id == onUpdateMessage.id;
      });
      if (
        msg &&
        msg.length &&
        msg[0].content &&
        onUpdateMessage.content &&
        msg[0].content.length > onUpdateMessage.content.length &&
        !onUpdateMessage.isLastMessage
      ) {
        // No need to replace this message as this contains the older content of message than the one which we have now.
        return;
      }

      if (this.project === KtznProject.LiveChat && currentConversationId === onUpdateMessage.messageConversationId) {
        // In case of ChatGPT, if the gpt-streaming already started, then we need to stop runningEllipsis,
        // Sometimes we might get the events in wrong sequences, so it might cause an issue like GPT-79
        // This condition is added to solve GPT-79 quickly!
        store.dispatch(ConversationAction.guestRunningEllipse, {
          conversation_id: currentConversationId,
          access_id: store.getters.accessId,
          status: "stop",
          target: "biz_guest",
        }); // laMondo-guest
      }
    }

    onUpdateMessage.createdAt = new Date(onUpdateMessage.createdAt).toISOString();
    if (
      onUpdateMessage.messageConversationId !== store.getters.currentConversationId &&
      this.project === KtznProject.Staff
    ) {
      return;
    }
    const conversationId = onUpdateMessage.messageConversationId;
    if (conversationId === currentConversationId) {
      if (this.project === KtznProject.Staff) {
        // Hack to display system messages translations in staff chat
        onUpdateMessage.lamondoSystemMessage = onUpdateMessage.content;
      }
      store.dispatch(ConversationAction.updateMessageInCurrentMessages, onUpdateMessage);
    } else if (conversationId === currentStaffConversationId) {
      store.dispatch(ConversationAction.updateMessageInCurrentMessagesWithStaff, onUpdateMessage);
    } else {
      console.log("Error in onUpdateMessage. Conversation is not current or staff.");
    }
  };

  /**
   * Handles event to add an unread message to the unread message list.
   * @param qcUnreadMessage QcUnreadMessage event
   */
  onCreateQcUnreadMessageHandler = (qcUnreadMessage: QcUnreadMessage): void => {
    console.log("Subscribe  onCreateQcUnreadMessage: ", qcUnreadMessage);
    // Run code only for a specific frontend projects (pwa-guest & Livechat-guest)
    switch (this.project) {
      case KtznProject.Guest:
        // ここにくるのは、スタッフがゲスト会話でメッセージを送った場合のみ
        // ゲスト会話でのスタッフのメッセージのみメッセージ未読として管理
        // Only come here if the staff sent a message in a guest conversation
        // Only staff messages in guest conversations are managed as unread messages
        if (store.getters.guest) {
          // ゲストの場合は未読一覧を取得
          // Get unread list for guests
          store.dispatch(ConversationAction.listUnReadMessage);
          // 未読作成を受け取ったことをdispatch
          // dispatch that you received an unread creation
          store.dispatch(ConversationAction.receivedCreateUnReadMessage, {
            conversation_id: qcUnreadMessage.conversation_id,
          });
        }
        return;
      case KtznProject.LiveChat:
        // Only come here if the livechat-staff sent a message in a livechat-guest conversation
        // Only staff messages in guest conversations are managed as unread messages
        if (store.getters.guest) {
          // dispatch that you received an unread creation
          store.dispatch(ConversationAction.receivedCreateUnReadMessage, {
            conversation_id: qcUnreadMessage.conversation_id,
          });
        }
        return;
      default:
        // pwaの場合に、createQcUnreadMessageを受け取るパターンはない
        // In case of pwa, there is no pattern to receive createQcUnreadMessage
        return;
    }
  };

  /**
   * [DEPRECATED] Handle event to delete an unread message.
   * Assuming that this handler will not be called anymore (unread messages are now deleted via readConversation).
   * @param qcUnreadMessage QcUnreadMessage event
   */
  onDeleteQcUnreadMessageHandler = (qcUnreadMessage: QcUnreadMessage): void => {
    console.log("Subscribe onDeleteQcUnreadMessage: ", qcUnreadMessage);
    // ここにはもうこなくなる想定(メッセージ未読が消されるのはreadConversation経由に統一)
  };

  /**
   * Handles event to mark all messages of a conversation as read.
   * @param readConversation ReadConversation event
   */
  onReadConversationHandler = (readConversation: ReadConversation): void => {
    console.log("Subscribe onReadConversation: ", readConversation);
    // readConversationが実行されるのは、
    // ゲストが会話を開いたときと、
    // ゲストが会話画面を開いているときにcreateQcUnreadMessageを受け取ったとき
    store.dispatch(ConversationAction.receivedReadConversation, {
      conversation_id: readConversation.conversation_id,
      user: readConversation.read_user,
      read_at: readConversation.read_at,
      unread_messages: readConversation.unread_messages,
    });
  };

  /**
   * Handles updating to conversation state.
   * @param conversationState ConversationState event
   */
  onUpdateConversationStateHandler = async (conversationState: ConversationState): Promise<void> => {
    console.log("Subscribe onUpdateConversationState: ", conversationState);
    const res = await new ConversationService().isConvMember(conversationState);
    if (res.isErr()) {
      return await new ErrorService().handleError(res.error);
    }
    const user = store.getters.user;
    if (user.userType === "biz_staff" || res.value) {
      await store.dispatch(ConversationAction.updateConversationState, {
        conversation_id: conversationState.conversation_id,
        conversation_state: conversationState.conversation_state,
        live_chat_strategy: conversationState.live_chat_strategy,
        machine_translated: conversationState.machine_translated,
      });
    }
  };

  /**
   * Handles the nofifyMyself event.
   *
   * The event is used to notify the user itself to do some action.
   * Like communication between some web and native app code.
   * E.g. Closing a webview in a native iOS app.
   * @param event NotifyMyself event
   */
  onNotifyMyselfHandler = (event: NotifyMyself): void => {
    console.log("Subscribe onNotifyMyself: ", event);
    store.dispatch(AuthAction.onNotifyMyself, event);
  };

  /**
   * Handles the change of a user.
   * @param changeUser ChangeUser event
   */
  onChangeUserHandler = (changeUser: ChangeUser): void => {
    console.log("Subscribe onChangeUser: ", changeUser);
    location.reload();
  };

  /**
   * Notifies a staff that a guest is currently asking for a service via chat (or call?).
   * @param onNotifyInquiry EarliestInquiryInfo event
   */
  onNotifyInquiryHandler = async (onNotifyInquiry: EarliestInquiryInfo): Promise<void> => {
    console.log("Subscribe onNotifyInquiry: ", onNotifyInquiry);
    const today = new Date();
    console.warn("Inquiry Re-Notification : ", today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds());
    // サブスクライブを受け取った時点で、すでに他の人が対応しているかもしれないので、再度取得し直す
    // By the time you receive the subscription, someone else may already be responding, so get it again.
    const user = store.getters.user;
    // !isEmpty check is needed because `user` can be `{}`, which is always true in JavaScript
    if (user && !isEmpty(user) && user.userType === "biz_staff") await checkExistInquiry(user.langCd);
  };

  /**
   * Handles the test event used for development or debugging.
   * @param event Test event string
   */
  testHandler = (event: string): void => {
    store.dispatch(ConversationAction.addEventDataList, event);
  };

  /**
   * Handles the CreateSandbox event.
   *
   * Currently used in the `fe-admin` project???
   * @param event SandboxResult event
   */
  onCreateSandboxHandler = async (event: SandboxResult): Promise<void> => {
    await store.dispatch(HotelAction.onNotifyCreateSandbox, event);
  };

  /**
   * Handles the ApplySandbox event.
   *
   * Currently used in the `fe-admin` project???
   * @param event SandboxResult event
   */
  onApplySandboxHandler = async (event: SandboxResult): Promise<void> => {
    await store.dispatch(HotelAction.onNotifyApplySandbox, event);
  };

  /**
   * Handles the CreateQcUnreadConversation event to show to bold ticket title for new message as unread from guest.
   *
   * Currently used in the `fe-staff` project.
   * @param event
   */
  onCreateQcUnreadConversation = async (event: CreateQcUnreadConversation): Promise<void> => {
    // ここにくるのは、普通の会話で誰かがメッセージを投稿した場合とゲスト会話でゲストが投稿した場合
    // to bold ticket title for new message as unread from guest
    console.log("Subscribe onCreateQcUnreadConversation: UNREAD ===============", event);
    await store.dispatch(ConversationAction.receivedCreateQcUnreadConversation, event);
  };

  /**
   * Event handler that is used to show green dot next to the ticket-title when last message is from guest and staff hasn't replied yet.
   *
   * Currently used in the `fe-staff` project.
   * @param createUnrepliedConv CreateQcUnrepliedConversation event
   */
  onCreateQcUnrepliedConversationHandler = async (
    createUnrepliedConv: CreateQcUnrepliedConversation
  ): Promise<void> => {
    // Run code only for a specific frontend project
    switch (this.project) {
      case KtznProject.Staff:
        console.log("Subscribe OnCreateQcUnrepliedConversation: UNNREPLIED ===============", createUnrepliedConv);
        await store.dispatch(ConversationAction.receivedCreateQcUnrepliedConversation, createUnrepliedConv);
        return;
      default:
        return;
    }
  };

  /**
   *
   * @param data
   */
  onStartVoiceChatInRoomHandler = (data: OnStartVoiceChatInRoom): void => {
    console.log("Subscribe OnStartVoiceChatInRoom: ", data);
    store.dispatch(ConversationAction.startVoiceChaInRoom, data);
  };

  /**
   *
   * @param data
   */
  onRespondVoiceChatInRoomHandler = (data: OnRespondVoiceChatInRoom): void => {
    console.log("Subscribe OnRespondVoiceChatInRoom: ", data);
    store.dispatch(ConversationAction.respondVoiceChatInRoom, data);
  };

  // /**
  //  *
  //  * @param data
  //  */
  // onFinishVoiceChatInRoomHandler = (data: OnFinishVoiceChatInRoom): void => {
  //   console.log("Subscribe OnFinishVoiceChatInRoom: ", data);
  //   if (data.conversation_id === store.getters.menuPath.conversationId && store.getters.guest) {
  //     store.commit(VoiceTalkMutation.SET_MODE, "call");
  //     store.commit(VoiceTalkMutation.SET_SESSION_ID, null);
  //   }
  // };

  /**
   * For showing running ellipse for guest and staff
   * Currently used in the `fe-livechat-guest` && 'fe-staff' projects of laMondo + fe-guest of inroom
   * @param data
   * @returns
   */
  onChatTyping = async (data: any): Promise<void> => {
    const user = store.getters.user;
    console.info("Subscribe to: ChatTyping: ", user.userType, data);
    if (user.userType === "biz_staff" && data.target === "biz_staff" && data.access_id === user.id) {
      await store.dispatch(ConversationAction.runningEllipse, data);
    } else if (user.userType === "biz_guest" && data.target === "biz_guest" && data.access_id === user.id) {
      if (this.project === KtznProject.LiveChat) await store.dispatch(ConversationAction.guestRunningEllipse, data); // laMondo-guest
      if (this.project === KtznProject.Guest)
        await store.commit(ConversationMutation.SET_PWA_GUEST_RUNNING_ELLIPSE, data); // inroom-guest
    } else {
      // nothing to do here, user should be guest or staff
    }
    return;
  };

  /**
   * For displaying new icon in case of new inquiries
   * @param data
   * @constructor
   */
  onDisplayNewIcon = async (data: {
    staff_group_id: number;
    access_ids: string[];
    conversation_id: string;
  }): Promise<void> => {
    console.info("Subscribe to DisplayNewIcon: ", data);
    if (data.access_ids.includes(store.getters.user.id)) {
      const today = new Date();
      console.warn(
        "Display New Icon - Re-Notification : ",
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds()
      );
      await store.dispatch(ConversationAction.showNewIcon);
    }
    return;
  };
}
